/**
 * BotsPage Component
 * Menu/list style design - compact and scannable
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { Sidebar } from '@/components/layout/Sidebar';
import { BotModal } from '@modules/chatbots/components/BotModal';
import { BotShareModal } from '@modules/chatbots/components/BotShareModal';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { SkillsManagementPage } from '@modules/skills/pages/SkillsManagementPage';
import { DataBundlesPage } from '@modules/data-bundles/pages/DataBundlesPage';
import { AgentOfAgentsPage } from '@modules/agent-of-agents/pages/AgentOfAgentsPage';
import botService from '@modules/chatbots/services/botService';
import { primeAgentColor } from '@modules/conversations/utils/agentColorCache';
import { useAuth, useFeature } from '@/hooks';
import { useLanguage } from '@/hooks/useLanguage';
import type { Bot } from '@/types/models';
import type { EditorTab } from '@modules/chatbots/components/BotModal';
import { showNotification } from '@/utils/showNotification';
import './BotsPage.css';
import BotListItem from '@modules/chatbots/components/BotListItem';

// Cache configuration. Per-tab (sessionStorage) AND per-user (key
// includes the active user_id) so a different user logging into the
// same tab can never see another user's bots — even before the
// authService's cache wipe runs. Defense in depth.
const BOTS_CACHE_PREFIX = 'bots_cache:';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheData {
  data: Bot[];
  timestamp: number;
}

export default function BotsPage() {
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const { t } = useLanguage();
  const { user, isAdmin, isGroupAdmin, isSuperAdmin } = useAuth();
  const cacheKey = BOTS_CACHE_PREFIX + (user?.user_id || 'anon');
  const [allBots, setAllBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'private' | 'shared'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBot, setEditingBot] = useState<Bot | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharingBot, setSharingBot] = useState<Bot | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    botId: string;
    botName: string;
    loading: boolean;
  }>({ isOpen: false, botId: '', botName: '', loading: false });
  const [duplicateModal, setDuplicateModal] = useState<{
    isOpen: boolean;
    botId: string;
    botName: string;
    loading: boolean;
  }>({ isOpen: false, botId: '', botName: '', loading: false });

  const canCreateBots = isAdmin || isGroupAdmin || isSuperAdmin;
  // Admins+ get the tabbed Agents page (Agents / Skills / Data Bundles).
  // Regular users see only the agents list (no tabs), exactly as before.
  const [activeTab, setActiveTab] = useState<'agents' | 'skills' | 'bundles' | 'aoa'>('agents');
  // Agent of Agents is a tab here too — gated by its feature flag + group_admin
  // (same access rule the standalone page had).
  const agentOfAgentsEnabled = useFeature('modules.agent_of_agents');
  const canUseAoA = agentOfAgentsEnabled && (isGroupAdmin || isSuperAdmin);
  // The Data Bundles tab STAYS VISIBLE on Library tenants, deliberately.
  // People need to see what they had — hiding the tab reads as "my bundles
  // are gone", which is the opposite of the truth (they were migrated, and
  // they are kept for rollback). The page shows a banner saying the Library
  // is where files are managed now, and the server refuses bundle writes on
  // Library tenants anyway, so seeing cannot turn into diverging.
  const showBundlesTab = true;

  useEffect(() => {
    loadBots();
  }, []);

  const loadBots = async () => {
    try {
      // Try to load from cache first
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const cacheData: CacheData = JSON.parse(cached);
          const isValid = Date.now() - cacheData.timestamp < CACHE_TTL;
          if (isValid && cacheData.data.length > 0) {
            setAllBots(cacheData.data);
            setLoading(false);
            // Refresh in background
            refreshBotsInBackground();
            return;
          }
        } catch (e) {
          console.error('[BotsPage] Failed to parse cache:', e);
        }
      }

      // No valid cache, load from API
      setLoading(true);
      const data = await botService.getBots();
      setAllBots(data);

      // Cache the results
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.error('[BotsPage] Failed to cache bots:', e);
      }
    } catch (error) {
      console.error('Error loading bots:', error);
      showNotification(t('bots.failedToLoadAgents'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshBotsInBackground = async () => {
    try {
      const data = await botService.getBots();
      setAllBots(data);
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('[BotsPage] Background refresh failed:', error);
    }
  };

  const invalidateCache = () => {
    sessionStorage.removeItem(cacheKey);
  };

  /**
   * Derived during render, NOT stored in state.
   *
   * This used to be state written by an effect keyed on [searchQuery,
   * filterType, allBots]. Effects run after paint, so the frame in which the
   * fetch resolved had `loading === false` and `allBots` populated while the
   * filtered list was still the initial `[]` — and that frame painted the
   * "No agents yet" empty state before the real list appeared. Computing it
   * here means the list and its inputs can never disagree, even for one frame.
   */
  const filteredBots = useMemo(() => {
    let filtered = [...allBots];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (bot) =>
          bot.chatbot_name.toLowerCase().includes(query) ||
          bot.chatbot_description?.toLowerCase().includes(query)
      );
    }

    if (filterType === 'private') {
      filtered = filtered.filter((bot) => bot.chatbot_visibility === 'private');
    } else if (filterType === 'shared') {
      filtered = filtered.filter(
        (bot) => bot.chatbot_visibility === 'team' || bot.chatbot_visibility === 'public'
      );
    }

    return filtered;
  }, [allBots, searchQuery, filterType]);

  const favoriteBots = filteredBots.filter((bot) => bot.is_favorited);
  const nonFavoriteBots = filteredBots.filter((bot) => !bot.is_favorited);

  const handleBotClick = (botId: string) => {
    // Prime the agent-color cache so the chat opens already painted in this
    // agent's color (no flash of the default while the bot fetch resolves).
    const bot = filteredBots.find((b) => b.chatbot_id === botId);
    if (bot) primeAgentColor(botId, bot.avatar_color, bot.chatbot_name || '');
    navigate(tenantPath(`/chat?bot=${botId}`));
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [editTab, setEditTab] = useState<EditorTab | undefined>(undefined);

  const openEditor = async (botId: string, tab?: EditorTab) => {
    const fullBot = await botService.getBot(botId);
    setEditTab(tab);
    setEditingBot(fullBot);
    setIsModalOpen(true);
  };

  // `?edit=<agent>&tab=connectors` opens that agent's editor on that tab —
  // the connector library links each agent that uses a connector this way.
  // The parameters are dropped once read, so closing the editor stays closed.
  useEffect(() => {
    const botId = searchParams.get('edit');
    if (!botId) return;
    const tab = searchParams.get('tab');
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    next.delete('tab');
    setSearchParams(next, { replace: true });
    openEditor(botId, tab === 'connectors' ? 'connectors' : undefined).catch(() => {
      showNotification(t('bots.failedToLoadDetails'), 'error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleEditBot = async (botId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await openEditor(botId);
    } catch (error) {
      console.error('Error loading bot details:', error);
      showNotification(t('bots.failedToLoadDetails'), 'error');
    }
  };

  const handleDuplicateBot = (botId: string, botName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDuplicateModal({ isOpen: true, botId, botName, loading: false });
  };

  const confirmDuplicateBot = async () => {
    if (!duplicateModal.botId) return;
    setDuplicateModal(prev => ({ ...prev, loading: true }));
    try {
      await botService.duplicateBot(duplicateModal.botId);
      showNotification(t('bots.duplicatedSuccess'), 'success');
      setDuplicateModal({ isOpen: false, botId: '', botName: '', loading: false });
      await loadBots();
    } catch (error: any) {
      console.error('Error duplicating bot:', error);
      const msg = error?.response?.data?.error || t('bots.failedToDuplicate');
      showNotification(msg, 'error');
      setDuplicateModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteBot = (botId: string, botName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteModal({ isOpen: true, botId, botName, loading: false });
  };

  const confirmDeleteBot = async () => {
    if (!deleteModal.botId) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));

    try {
      await botService.deleteBot(deleteModal.botId);
      showNotification(t('bots.deleted'), 'success');
      invalidateCache();
      setDeleteModal({ isOpen: false, botId: '', botName: '', loading: false });
      await loadBots();
    } catch (error) {
      console.error('Error deleting bot:', error);
      showNotification(t('bots.failedToDelete'), 'error');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleFavorite = async (botId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const bot = allBots.find((b) => b.chatbot_id === botId);
    const currentlyFavorited = bot?.is_favorited ?? false;

    try {
      await botService.toggleFavorite(botId, currentlyFavorited);
      setAllBots((prev) =>
        prev.map((b) =>
          b.chatbot_id === botId ? { ...b, is_favorited: !b.is_favorited } : b
        )
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleCreateBot = () => {
    setEditingBot(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingBot(null);
  };

  const handleModalSuccess = () => {
    invalidateCache();
    loadBots();
  };

  const handleShareBot = (botId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const bot = allBots.find((b) => b.chatbot_id === botId);
    if (bot) {
      setSharingBot(bot);
      setIsShareModalOpen(true);
    }
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
    setSharingBot(null);
  };

  // The row itself lives in BotListItem so the White-Label preview renders the
  // REAL agent row rather than a lookalike of it. This page keeps only the
  // behaviour; the markup has exactly one home.
  const renderBotItem = (bot: Bot) => (
    <BotListItem
      key={bot.chatbot_id}
      bot={bot}
      onOpen={handleBotClick}
      onToggleFavorite={handleToggleFavorite}
      onEdit={handleEditBot}
      onDuplicate={handleDuplicateBot}
      onShare={handleShareBot}
      onDelete={handleDeleteBot}
    />
  );

  return (
    <>
      <div className="bots-layout">
        <Sidebar
          options={{
            showHome: true,
            showChat: false,
            showBots: true,
            showSearch: true,
            showAdmin: true,
            showGroupAdmin: true,
            showSuperAdmin: true,
            showConversations: true,
            showBotsList: true,
            activeItem: 'bots',
          }}
        />

        <main className="bots-main">
          <div className="bots-content">
            {/* Tab bar — the Agents page now hosts Skills + Data Bundles as
                tabs (admins only). Regular users see just the agents list. */}
            {canCreateBots && (
              <div className="agents-tabs">
                <button className={`agents-tab-btn ${activeTab === 'agents' ? 'active' : ''}`} onClick={() => setActiveTab('agents')}>{t('agents.title')}</button>
                <button className={`agents-tab-btn ${activeTab === 'skills' ? 'active' : ''}`} onClick={() => setActiveTab('skills')}>{t('skills.title')}</button>
                {showBundlesTab && (
                  <button className={`agents-tab-btn ${activeTab === 'bundles' ? 'active' : ''}`} onClick={() => setActiveTab('bundles')}>{t('dataBundles.title')}</button>
                )}
                {canUseAoA && (
                  <button
                    className={`agents-tab-btn ${activeTab === 'aoa' ? 'active' : ''}`}
                    onClick={() => setActiveTab('aoa')}
                    data-testid="tab-agent-of-agents"
                  >
                    {t('metaAgent.nav.label')}
                  </button>
                )}
              </div>
            )}

            {activeTab === 'agents' && (
            <>
            {/* Header — title + subtitle only, like Data Bundles */}
            <div className="simple-chat-header">
              <h1 dir="auto" className="bot-title">{t('agents.title')}</h1>
              <p className="text-muted">{t('agents.subtitle')}</p>
            </div>

            {/* Controls row — below the separator line, like Data Bundles */}
            <div className="bots-controls">
              <div className="bots-search-box">
                <svg className="bots-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input dir="auto"
                  type="text"
                  className="bots-search-input"
                  placeholder={t('admin.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                className="bots-filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'private' | 'shared')}
              >
                <option value="all">{t('common.filterAll')}</option>
                <option value="private">{t('agentModal.private')}</option>
                <option value="shared">{t('common.shared')}</option>
              </select>

              {canCreateBots && (
                <button className="bots-create-btn" data-testid="bots-create-button" onClick={handleCreateBot}>
                  <svg viewBox="0 0 24 24">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                  {t('dashboard.newAgent')}
                </button>
              )}
            </div>

            {/* Content — scrollable area */}
            <div className="bots-list-container">
            {loading ? (
              <div className="bots-loading">
                <div className="bots-loading-spinner" />
                <div className="bots-loading-text">{t('common.loading')}</div>
              </div>
            ) : filteredBots.length === 0 ? (
              <div className="bots-empty">
                <div className="bots-empty-icon">🤖</div>
                <h3>{t('agents.noAgentsYet')}</h3>
                <p>{canCreateBots ? t('bots.createFirstAgent') : t('bots.noAgentsSharedWithYou')}</p>
                {canCreateBots && (
                  <button className="bots-empty-btn" onClick={handleCreateBot}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                    {t('bots.createBot')}
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Favorites */}
                {favoriteBots.length > 0 && (
                  <section className="bots-section">
                    <div className="bots-section-header">
                      <h2 className="bots-section-title">
                        <span className="bots-section-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        </span>
                        {t('dashboard.agents.tabs.favorites')}
                      </h2>
                    </div>
                    <div className="bots-list">
                      {favoriteBots.map((bot) => renderBotItem(bot))}
                    </div>
                  </section>
                )}

                {/* All Bots */}
                {nonFavoriteBots.length > 0 && (
                  <section className="bots-section">
                    <div className="bots-section-header">
                      <h2 className="bots-section-title">
                        <span className="bots-section-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="10" rx="2" />
                            <circle cx="8.5" cy="16" r="1.5" fill="currentColor" />
                            <circle cx="15.5" cy="16" r="1.5" fill="currentColor" />
                            <path d="M9 7V4h6v3" />
                          </svg>
                        </span>
                        {favoriteBots.length > 0 ? t('agents.allAgents') : t('agents.myAgents')}
                      </h2>
                    </div>
                    <div className="bots-list">
                      {nonFavoriteBots.map((bot) => renderBotItem(bot))}
                    </div>
                  </section>
                )}
              </>
            )}

            {/* Mobile bottom create button */}
            {canCreateBots && (
              <button className="bots-mobile-create-btn" onClick={handleCreateBot}>
                <svg viewBox="0 0 24 24">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                {t('dashboard.newAgent')}
              </button>
            )}
            </div>
            </>
            )}

            {activeTab === 'skills' && <SkillsManagementPage embedded />}
            {activeTab === 'bundles' && showBundlesTab && <DataBundlesPage embedded />}
            {activeTab === 'aoa' && canUseAoA && <AgentOfAgentsPage embedded />}
          </div>
        </main>
      </div>

      <BotModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        bot={editingBot}
        initialTab={editTab}
      />

      {sharingBot && (
        <BotShareModal
          isOpen={isShareModalOpen}
          onClose={handleShareModalClose}
          botId={sharingBot.chatbot_id}
          botName={sharingBot.chatbot_name}
        />
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, botId: '', botName: '', loading: false })}
        onConfirm={confirmDeleteBot}
        title={t('agents.deleteAgent')}
        message={`Are you sure you want to permanently delete "${deleteModal.botName}"?\n\nThis will remove the agent and all its configurations.\n\nThis action cannot be undone.`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteModal.loading}
      />

      <ConfirmModal
        isOpen={duplicateModal.isOpen}
        onClose={() => setDuplicateModal({ isOpen: false, botId: '', botName: '', loading: false })}
        onConfirm={confirmDuplicateBot}
        title={t('agents.duplicateAgent')}
        message={`Create a copy of "${duplicateModal.botName}"?\n\nThe duplicate will include the same system prompt, files, data bundles, and skills.`}
        confirmText={t('common.duplicate')}
        cancelText={t('common.cancel')}
        variant="info"
        loading={duplicateModal.loading}
      />
    </>
  );
}
